import { useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Layers, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ZoneOption {
  id: string;
  name: string;
  color?: string | null;
}

interface Props {
  zones: ZoneOption[];
  highlighted: Set<string>;
  onChange: (next: Set<string>) => void;
  /** optional occupancy badge map (`occupied/total`) */
  occupancy?: Map<string, { occupied: number; total: number }>;
}

/**
 * Étape 8 — Zone filter (multi-select highlight).
 * Empty set = no highlight (all zones full opacity).
 * Non-empty = highlight checked zones, dim the others.
 */
export const V2ZoneFilterSelect = ({ zones, highlighted, onChange, occupancy }: Props) => {
  const label = useMemo(() => {
    if (highlighted.size === 0) return "Toutes les zones";
    if (highlighted.size === 1) {
      const z = zones.find(z => z.id === Array.from(highlighted)[0]);
      return z?.name ?? "1 zone";
    }
    return `${highlighted.size} / ${zones.length} zones`;
  }, [highlighted, zones]);

  const toggle = (id: string) => {
    const next = new Set(highlighted);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  return (
    <div className="flex items-center gap-1">
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" className="h-9 gap-2">
            <Layers className="h-4 w-4" />
            <span className="text-sm">{label}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-60 p-2">
          <div className="flex items-center justify-between px-2 pb-2 text-xs text-muted-foreground">
            <span>Filtrer les zones</span>
            {highlighted.size > 0 && (
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => onChange(new Set())}
              >
                Tout afficher
              </button>
            )}
          </div>
          <ul className="space-y-0.5 max-h-72 overflow-auto">
            {zones.map((z) => {
              const checked = highlighted.has(z.id);
              const stat = occupancy?.get(z.id);
              return (
                <li key={z.id}>
                  <label
                    className={cn(
                      "flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm",
                      checked && "bg-muted/60",
                    )}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggle(z.id)} />
                    <span className="w-2 h-2 rounded-full" style={{ background: z.color || "#6366f1" }} />
                    <span className="flex-1 truncate">{z.name}</span>
                    {stat && stat.total > 0 && (
                      <span className="text-[10px] font-mono text-muted-foreground tabular-nums">
                        {stat.occupied}/{stat.total}
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
      {highlighted.size > 0 && (
        <Button
          size="sm"
          variant="ghost"
          className="h-9 w-9 p-0"
          onClick={() => onChange(new Set())}
          aria-label="Effacer le filtre"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
